import { Combobox, createListCollection, Text } from '@chakra-ui/react'
import { Check, ChevronsUpDown } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { ComponentProps } from 'react'

/**
 * Reserved sentinel for the optional "None" / clear entry. Using a
 * non-guessable string so a real option's `id` can't collide. Consumers
 * must not emit this as a real option `id`.
 */
const NONE_VALUE = '__searchable_combobox_none__'

export interface SearchableComboboxOption {
  /** Stable unique key. Emitted through `onChange` when selected. */
  id: string
  /** Visible text in the dropdown row and in the input after selection. */
  label: string
  /**
   * Lower-cased text matched against the user's query. Defaults to the
   * lower-cased `label` when omitted. Pass extra tokens here (e.g. an
   * extension number alongside its name) to make them searchable.
   */
  searchText?: string
}

export interface SearchableComboboxProps {
  options: SearchableComboboxOption[]
  /**
   * Currently selected option id. Empty string means "no selection"
   * (whether or not the caller opts into rendering the None row).
   */
  value: string
  onChange: (value: string) => void
  /** Placeholder shown when the input is empty. */
  placeholder?: string
  /** Screen-reader label for the search input. */
  searchAriaLabel?: string
  /** Message shown inside the popover when no options match the search. */
  emptyMessage?: string
  /**
   * When provided, renders a "None" row at the top of the list that is
   * always visible regardless of the search term — so users can clear
   * the selection without backing out the typed text first. Picking it
   * emits an empty string through `onChange`.
   */
  noneOptionLabel?: string
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
  width?: string | number
  /** Max height of the dropdown before it scrolls. */
  maxH?: string
  /** Forwarded as the `id` attribute on the underlying input. */
  id?: string
  /**
   * Extra props spread onto the underlying `Combobox.Input`. Use this for
   * styling overrides that should match the rest of a form's inputs —
   * e.g. `{ bg: 'bg.subtle', borderColor: 'border.default' }` to align
   * with native selects on the same page. `id`, `placeholder`, and
   * `aria-label` are excluded because they're set through dedicated props.
   */
  inputProps?: Omit<
    ComponentProps<typeof Combobox.Input>,
    'id' | 'placeholder' | 'aria-label'
  >
}

/**
 * Searchable combobox over a caller-supplied list of options.
 *
 * Extracted from `ExtensionCombobox` and `IntegrationCombobox` in
 * `libraries/phone-numbers-ui/`, which are near-identical hand-rolled
 * versions of the same UX. Future consumers should use this component
 * and pre-shape their domain objects into `SearchableComboboxOption`
 * entries rather than copy-pasting the filtering/state dance below.
 *
 * **Behaviors worth knowing:**
 *
 * 1. The optional "None" row (via `noneOptionLabel`) stays visible
 *    regardless of the search term so clearing a selection never
 *    requires backspacing the query first.
 * 2. The input is filled with the selected option's label after each
 *    pick; focus on the input selects that text so typing immediately
 *    replaces it instead of appending. The `select()` is queued through
 *    `requestAnimationFrame` because Ark UI's own focus handler would
 *    otherwise reset the cursor on the same tick.
 * 3. Closing the popover without picking anything (escape, click-away,
 *    blur) restores the input to the selected option's label — so a
 *    stale query doesn't stick around showing the wrong label.
 */
export const SearchableCombobox: React.FC<SearchableComboboxProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  searchAriaLabel,
  emptyMessage = 'No matches',
  noneOptionLabel,
  disabled,
  size = 'md',
  width = '100%',
  maxH = '280px',
  id,
  inputProps,
}) => {
  const selectedOption = useMemo(() => {
    if (!value) return null
    return options.find((option) => option.id === value) ?? null
  }, [options, value])

  const [inputValue, setInputValue] = useState(selectedOption?.label ?? '')

  // Tracks whether the popover just closed because the user picked an
  // option. The `onValueChange` path sets `inputValue` to the new label;
  // without this guard, `onOpenChange`'s stale-closure branch would
  // overwrite it with the previous selection's label before the parent's
  // `value` has propagated back down.
  const justSelectedRef = useRef(false)

  // Keep the rendered label in sync when `value` changes from outside
  // (e.g. a parent reset) or when `options` loads late and can finally
  // resolve the selected option's label.
  useEffect(() => {
    setInputValue(selectedOption?.label ?? '')
  }, [selectedOption])

  const filteredOptions = useMemo(() => {
    const trimmed = inputValue.trim().toLowerCase()
    const matchesSelected =
      !!selectedOption && selectedOption.label.trim().toLowerCase() === trimmed
    if (!trimmed || matchesSelected) return options
    return options.filter((option) => {
      // Defensive `.toLowerCase()` on the haystack even though `searchText`
      // is documented as pre-lowered — consumers sometimes pass it raw.
      const haystack = option.searchText ?? option.label
      return haystack.toLowerCase().includes(trimmed)
    })
  }, [options, inputValue, selectedOption])

  const visibleOptions = useMemo<SearchableComboboxOption[]>(() => {
    if (noneOptionLabel === undefined) return filteredOptions
    return [
      { id: NONE_VALUE, label: noneOptionLabel, searchText: '' },
      ...filteredOptions,
    ]
  }, [filteredOptions, noneOptionLabel])

  const collection = useMemo(
    () =>
      createListCollection<SearchableComboboxOption>({
        items: visibleOptions,
        itemToValue: (item) => item.id,
        itemToString: (item) => item.label,
      }),
    [visibleOptions],
  )

  const comboboxValue = value
    ? [value]
    : noneOptionLabel !== undefined
      ? [NONE_VALUE]
      : []

  const hasSearchTerm = inputValue.trim().length > 0
  const isSearchMatchingSelected =
    !!selectedOption &&
    selectedOption.label.trim().toLowerCase() ===
      inputValue.trim().toLowerCase()
  const showNoMatchesHint =
    hasSearchTerm && !isSearchMatchingSelected && filteredOptions.length === 0

  return (
    <Combobox.Root
      collection={collection}
      value={comboboxValue}
      inputValue={inputValue}
      onValueChange={(details) => {
        const next = details.value[0] ?? ''
        justSelectedRef.current = true
        if (!next || next === NONE_VALUE) {
          onChange('')
          setInputValue('')
          return
        }
        onChange(next)
        const match = options.find((option) => option.id === next)
        setInputValue(match?.label ?? '')
      }}
      onInputValueChange={(details) => {
        setInputValue(details.inputValue)
      }}
      onOpenChange={(details) => {
        if (details.open) return
        if (justSelectedRef.current) {
          justSelectedRef.current = false
          return
        }
        setInputValue(selectedOption?.label ?? '')
      }}
      openOnClick
      disabled={disabled}
      width={width}
      size={size}
    >
      <Combobox.Control>
        <Combobox.Input
          {...inputProps}
          id={id}
          placeholder={placeholder}
          aria-label={searchAriaLabel}
          onFocus={(e) => {
            // Run a consumer-provided onFocus first so behaviors like
            // analytics or focus-logging aren't silently dropped by
            // the select-on-focus override below.
            inputProps?.onFocus?.(e)
            const input = e.currentTarget
            requestAnimationFrame(() => input.select())
          }}
        />
        <Combobox.IndicatorGroup>
          <Combobox.Trigger>
            <ChevronsUpDown />
          </Combobox.Trigger>
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      <Combobox.Positioner>
        <Combobox.Content maxH={maxH} overflowY="auto">
          {showNoMatchesHint && (
            <Text
              px={3}
              py={2}
              fontSize="sm"
              color="fg.muted"
              role="status"
              aria-live="polite"
            >
              {emptyMessage}
            </Text>
          )}
          {visibleOptions.map((option) => (
            <Combobox.Item key={option.id} item={option}>
              <Combobox.ItemText>{option.label}</Combobox.ItemText>
              <Combobox.ItemIndicator>
                <Check />
              </Combobox.ItemIndicator>
            </Combobox.Item>
          ))}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  )
}
